import React, {
    useCallback,
    useState,
    useEffect,
    useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FacetedSearchCompact from './FacetedSearchCompact';
import { AccordionPanel, Accordion } from '../Components/Accordion';
import { query } from '../Actions/FacetedSearch.action';
import SortCriteriaCompact from './SortCriteriaCompact';
import FilterTag from './FilterTags';
import { translate } from '../Services/translation';
import { updateFilterOption } from '../Actions/FacetedSearch.action';
import { PRODUCT_VIEW_CACHED } from '../constants';

const FacetedSearchCompactContainer = () => {
    const dispatch = useDispatch();
    const {
        subNavigation,
        sortCriteria,
        facetFilters: globalFacetFilters,
        navigationTheme,
        productsViewCachedId,
    } = useSelector((state) => state.facetedSearch);

    const [facetFilters, setFacetFilters] = useState(globalFacetFilters || []);
    // sorting is hidden while the filter panel is open, to keep the compact view calm
    const [filterOpen, setFilterOpen] = useState(false);
    // wraps the accordion, sorting and selected filter tags. Clicks anywhere in here
    // (e.g. removing a filter tag) must not count as "outside" and close the filter panel.
    // The tag check by class is a fallback for third-party scripts that move the nodes around.
    const rootRef = useRef(null);
    const isClickInsideFilter = useCallback(
        (target) =>
            (rootRef.current && rootRef.current.contains(target)) ||
            !!(target && target.closest && target.closest('.selected-filter')),
        []
    );
    const previousCachedId = useRef(productsViewCachedId);
    useEffect(() => {
        if (previousCachedId.current !== productsViewCachedId) {
            setFacetFilters(globalFacetFilters);
            previousCachedId.current = productsViewCachedId;
        }
    }, [globalFacetFilters, productsViewCachedId]);

    useEffect(() => {
        dispatch(query(window.location.search.substr(1) || '', false));
    }, [dispatch]);

    const onFacetChange = useCallback(
        (filter, option) => {
            setFacetFilters((prevFacetFilters) => {
                return updateFilterOption(prevFacetFilters, filter, option);
            });
        },
        [setFacetFilters]
    );

    const onSearchResultDataChange = (dom) => {
        if ([null, undefined].includes(dom)) {
            return;
        }
        const container = document.createElement('div');
        container.innerHTML = dom;
        const existingResult = document.querySelector('#search-result');
        const tempResult = container.querySelector('#search-result');
        const replace = (node, newNode) =>
            node.parentNode.replaceChild(newNode, node);
        // move the React-owned filter nodes from existingResult to tempResult,
        // so the mounted components survive the swap (both desktop and compact)
        ['#facetedSearch', '#facetedSearchCompact'].forEach((selector) => {
            const existingFilter = existingResult.querySelector(selector);
            const tempFilter = tempResult.querySelector(selector);
            if (existingFilter && tempFilter) {
                replace(tempFilter, existingFilter);
            }
        });
        // replace existingResult with tempResult ( newResult )
        replace(existingResult, tempResult);
        // bootstrap react components if any exists in the search result
        window.__litium.bootstrapComponents();
    };

    useEffect(() => {
        const productViewCached = window.__litium.cache
            ? window.__litium.cache[PRODUCT_VIEW_CACHED] || {}
            : {};
        if (!productViewCached.used) {
            productViewCached.used = true;
            const dom = productViewCached.productsView;
            dom && onSearchResultDataChange(dom);
        }
    });

    const empty = (array) => !(array && array.length);

    const subNavigations = !subNavigation ? null : [subNavigation];
    const sortCriterias =
        !sortCriteria || !sortCriteria.sortItems
            ? null
            : sortCriteria.sortItems;

    const hidden = [subNavigations, facetFilters, sortCriterias].every((arr) =>
        empty(arr)
    );

    return hidden ? null : (
        <div ref={rootRef}>
            <Accordion
                className="compact-filter"
                closeOnClickOutside
                isClickInside={isClickInsideFilter}
                onToggle={(openIndex) => setFilterOpen(openIndex !== -1)}
            >
                {!empty(facetFilters) && (
                    <AccordionPanel header={translate('facet.header.filter')}>
                        <FacetedSearchCompact
                            {...{
                                facetFilters,
                                onFacetChange,
                            }}
                        />
                    </AccordionPanel>
                )}
            </Accordion>
            {!empty(sortCriterias) && (
                <SortCriteriaCompact
                    {...{
                        sortCriteria: sortCriterias,
                        hidden: filterOpen && !empty(facetFilters),
                    }}
                />
            )}
            <FilterTag
                {...{
                    facetFilters: globalFacetFilters,
                    navigationTheme: navigationTheme || '',
                }}
            />
        </div>
    );
};

export default FacetedSearchCompactContainer;
